/**
 * `useSystemMode()` — TanStack Query read hook for the deployment's runtime
 * mode.
 *
 * AC-1619 (epic AC-1493 — BYOK LLM credentials). The API Keys settings page
 * uses this to decide whether per-user (BYOK) keys are honored by the runtime
 * (`byok_mode === true`) or the deployment is in "Friends & Family" /
 * platform-managed mode (`byok_mode === false`; per-user keys ignored,
 * platform env keys used instead).
 *
 * ## Endpoint
 *
 * `GET /api/v1/system/mode` — returns
 * `{ byok_mode: boolean, supported_providers: string[] }`.
 *
 * ## Cache key
 *
 * `queryKeys.systemMode()` → `['systemMode']`. Deployment-scoped (not user-
 * or ID-scoped), so no ID threads through the key — mirrors `apiKeys()` and
 * `llmCatalog()`.
 *
 * Why a separate hook (and not the Zustand `llmByokMode` flag): the settings
 * page must reflect the authoritative system-mode endpoint for correctness.
 * The store mirror can drift; the endpoint is the source of truth here.
 */

import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import apiService from '../../services/api';
import { SystemMode } from '../../types';
import { queryKeys } from './queryKeys';

export type UseSystemModeOptions = Omit<
  UseQueryOptions<SystemMode, Error, SystemMode, ReturnType<typeof queryKeys.systemMode>>,
  'queryKey' | 'queryFn'
>;

/**
 * Returns the standard `UseQueryResult<SystemMode>`.
 *
 * @example
 *   const { data: mode } = useSystemMode();
 *   const byokMode = mode?.byok_mode ?? true; // default to BYOK when unknown
 */
export function useSystemMode(
  options?: UseSystemModeOptions,
): UseQueryResult<SystemMode, Error> {
  return useQuery<SystemMode, Error, SystemMode, ReturnType<typeof queryKeys.systemMode>>({
    queryKey: queryKeys.systemMode(),
    queryFn: () => apiService.getSystemMode(),
    ...options,
  });
}

export default useSystemMode;
